import { useCallback, useEffect, useState } from "react";
import Hero from "@/components/Hero";
import FinalCTA from "@/components/FinalCTA";
import { Check, ZoomIn, X, ChevronLeft, ChevronRight } from "lucide-react";

function Lightbox({ open, index, images, onClose, onPrev, onNext }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNext();
      if (e.key === "ArrowLeft") onPrev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, onPrev, onNext]);

  if (!open || !images.length) return null;
  const img = images[index];
  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4" data-testid="product-lightbox">
      <button onClick={onClose} className="absolute top-5 right-5 text-white p-3 rounded-full hover:bg-white/10" aria-label="Chiudi">
        <X size={26} />
      </button>
      <button onClick={onPrev} className="absolute left-3 sm:left-8 text-white p-3 rounded-full bg-white/10 hover:bg-white/20" aria-label="Precedente">
        <ChevronLeft size={28} />
      </button>
      <button onClick={onNext} className="absolute right-3 sm:right-8 text-white p-3 rounded-full bg-white/10 hover:bg-white/20" aria-label="Successivo">
        <ChevronRight size={28} />
      </button>
      <figure className="max-w-5xl w-full">
        <img src={img.src} alt={img.alt} className="w-full max-h-[80vh] object-contain rounded-md shadow-2xl" />
        <figcaption className="text-center text-white/80 mt-4">{img.alt} — <span className="text-white/60">{index + 1} / {images.length}</span></figcaption>
      </figure>
    </div>
  );
}

export default function ProductCategoryPage({ data }) {
  const galleryImages = data.images.slice(data.sectionImageCount || data.sections.length);
  const allImages = galleryImages.length ? galleryImages : data.images;
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const onPrev = useCallback(() => setIdx((i) => (i - 1 + allImages.length) % allImages.length), [allImages.length]);
  const onNext = useCallback(() => setIdx((i) => (i + 1) % allImages.length), [allImages.length]);

  return (
    <>
      <Hero
        testId={`${data.key}-hero`}
        desktopImg={data.hero.desktop}
        mobileImg={data.hero.mobile}
        eyebrow="Produzione industriale conto terzi"
        title={data.hero.title}
        subtitle={data.hero.subtitle}
        ctaLabel="Richiedi una consulenza"
      />

      <section className="py-20 bg-white" data-testid={`${data.key}-intro`}>
        <div className="max-w-[1120px] mx-auto px-5 lg:px-10">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="inline-block w-10 h-[3px] bg-drosun-red" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-drosun-muted">{data.eyebrow}</span>
          </div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl text-drosun-text leading-tight">{data.introTitle}</h2>
          <div className="mt-7 space-y-5 text-[17px] leading-relaxed text-drosun-muted">
            {data.intro.map((p) => <p key={p}>{p}</p>)}
          </div>
        </div>
      </section>

      <section className="bg-drosun-light py-20" data-testid={`${data.key}-content`}>
        <div className="max-w-[1320px] mx-auto px-5 lg:px-10 space-y-16 lg:space-y-20">
          {data.sections.map((section, i) => {
            const sectionImageCount = data.sectionImageCount ?? data.sections.length;
            const image = i < sectionImageCount ? data.images[i] : null;
            const reverse = i % 2 === 1;
            return (
              <article key={section.title} className={`reveal grid grid-cols-1 lg:grid-cols-2 gap-9 lg:gap-14 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div className="rounded-2xl overflow-hidden border border-neutral-200 bg-white shadow-[0_15px_50px_-30px_rgba(0,0,0,0.4)]">
                  {image ? (
                    <img src={image.src} alt={image.alt} className="w-full aspect-[4/3] object-cover" loading="lazy" />
                  ) : (
                    <div className="aspect-[4/3] bg-drosun-navy" aria-hidden />
                  )}
                </div>
                <div>
                  <h2 className="font-display uppercase text-3xl sm:text-4xl text-drosun-text leading-tight">{section.title}</h2>
                  <div className="mt-5 space-y-4 text-[16.5px] leading-relaxed text-drosun-muted">
                    {section.paragraphs.map((p) => <p key={p}>{p}</p>)}
                  </div>
                  {section.subsections?.length > 0 && (
                    <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {section.subsections.map((sub) => (
                        <div key={sub.title} className="rounded-xl bg-white border border-neutral-200 p-5">
                          <h3 className="font-display uppercase text-xl text-drosun-text">{sub.title}</h3>
                          {sub.text && <p className="mt-2 text-[15px] leading-relaxed text-drosun-muted">{sub.text}</p>}
                          {sub.items?.length > 0 && (
                            <ul className="mt-3 space-y-2">
                              {sub.items.map((item) => (
                                <li key={item} className="flex items-start gap-2 text-[14.5px] text-drosun-text">
                                  <Check size={17} className="text-drosun-red mt-0.5 shrink-0" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {galleryImages.length > 0 && (
        <section className="py-20 bg-white" data-testid={`${data.key}-gallery`}>
          <div className="max-w-[1320px] mx-auto px-5 lg:px-10">
            <div className="text-center max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="inline-block w-10 h-[3px] bg-drosun-red" />
                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-drosun-muted">Galleria</span>
              </div>
              <h2 className="font-display uppercase text-4xl sm:text-5xl text-drosun-text">{data.galleryTitle}</h2>
              <p className="mt-4 text-[17px] text-drosun-muted">{data.gallerySubtitle}</p>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {galleryImages.map((g, i) => (
                <button
                  key={g.src}
                  onClick={() => { setIdx(i); setOpen(true); }}
                  className="gal-card group relative overflow-hidden rounded-2xl border border-neutral-200 bg-drosun-light shadow-[0_15px_50px_-30px_rgba(0,0,0,0.45)] focus:outline-none focus:ring-2 focus:ring-drosun-red"
                  aria-label={g.alt}
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={g.src} alt={g.alt} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="gal-overlay absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex items-end justify-between p-5">
                    <span className="text-white text-sm font-semibold tracking-wide">{g.alt}</span>
                    <span className="w-10 h-10 rounded-full bg-white/95 text-drosun-navy inline-flex items-center justify-center shadow"><ZoomIn size={18} /></span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <Lightbox open={open} index={idx} images={allImages} onClose={() => setOpen(false)} onPrev={onPrev} onNext={onNext} />

      <FinalCTA
        testId={`${data.key}-final-cta`}
        title={data.cta.title}
        subtitle={data.cta.subtitle}
        primary={{ label: "Richiedi Informazioni", to: "/contatti" }}
        secondary={null}
      />
    </>
  );
}
